import { IContextualMenuItem, IIconProps } from '@fluentui/react';
import AppContext, { IAppContext } from 'App/AppContext';
import {
  IPActionButton,
  IPButtonProps,
  IPCommandBarButton,
  IPDefaultButton,
  IPIconButton,
  IPSpinner,
  IPText,
} from 'baseComponents';
import { IPContextualMenuItemType } from 'baseComponents/ContextualMenu/IPContextualMenu';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { isCopilotBusy } from 'components/Copilot/copilotPanelAvailability';
import {
  aiBackgroundIcon,
  aiInlineIcon,
  aiProcessIcon,
  chatBotIcon,
  commentIcon,
  copilotIconProps,
  globalStackTokensGapSmall,
  stopIcon,
} from 'globalStyles';
import { AIActionBinding, AIActionGroup, getAIActionLabel } from 'models/copilot/actions/aiActionBinding';
import { TFunction } from 'i18next';
import { AIActionKind } from 'models/copilot/actions/aiActionTemplate';
import { CopilotActiveField } from 'models/copilot/copilotField';
import { AIEnabled } from 'models/setting';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { apiSetSetting } from 'services/Api/settingService';
import { apiRequest } from 'services/Auth/authConfig';
import {
  FeatureTypes,
  hasRoleFeature,
  hasSubscriptionFeature,
  hasUserFeature,
  hasUserFeatureGenericManager,
} from 'services/Auth/featurePermissions';
import { SubscriptionTypes } from 'utils/subscription';

/**
 * Whether this user can use AI here, and if not, what the button offers instead:
 * - `available`: the panel is available (the same answer as `isCopilotPanelAvailable`);
 * - `off`: the plan and the role have AI, the organization has it switched off;
 * - `plan`: the role would have AI, the plan has none;
 * - `hidden`: no button at all (onboarding, a role without AI, a portal or container tenant).
 */
export type AIAccess = 'available' | 'off' | 'plan' | 'hidden';

export const getAIAccess = (appContext: IAppContext): AIAccess => {
  if (appContext.isOnboardingInProgress) return 'hidden';

  const login = appContext.user.login;
  if (login.subscriptionType === SubscriptionTypes.Portal || login.subscriptionType === SubscriptionTypes.Container) {
    return 'hidden';
  }
  if (!hasRoleFeature(login.userSystemRoles, FeatureTypes.AIAssistant)) return 'hidden';
  if (!hasSubscriptionFeature(login.subscriptionType, FeatureTypes.AIAssistant)) return 'plan';
  if (!hasUserFeature(appContext, FeatureTypes.AIAssistant)) return 'hidden';
  if (appContext.globalDataCache.settings.get(AIEnabled) !== true) return 'off';

  return 'available';
};

// A scene hands its bindings whatever the AI state; the button decides what can run. Without access at all there is
// nothing to show.
export const getOfferedAIActionBindings = (bindings: AIActionBinding[], appContext: IAppContext): AIActionBinding[] =>
  getAIAccess(appContext) === 'hidden' ? [] : bindings;

// Dispatch follows the template kind: an inline action runs in the scene, the panel kinds go through the
// bridge, which opens the panel and lets it run the action.
export const invokeAIAction = (
  binding: AIActionBinding,
  appContext: IAppContext,
  getActiveField?: () => CopilotActiveField | undefined,
): void => {
  const { template } = binding;
  switch (template.kind) {
    case AIActionKind.Inline:
      void template.run(binding.params);

      return;
    case AIActionKind.Assistant:
      appContext.copilotBridge.openFromUser();

      return;
    case AIActionKind.Prompt:
    case AIActionKind.Process:
      appContext.copilotBridge.requestAction({
        binding,
        activeField: getActiveField?.(),
        selectedEntityIds: binding.selectedEntityIds,
      });

      return;
  }
};

export const getAIActionIcon = (binding: AIActionBinding): IIconProps | undefined => {
  if (binding.icon) return binding.icon;
  if (binding.template.icon) return binding.template.icon;
  if (binding.group === AIActionGroup.Background) return binding.running ? stopIcon : aiBackgroundIcon;

  switch (binding.template.kind) {
    case AIActionKind.Inline:
      return aiInlineIcon;
    case AIActionKind.Prompt:
      return commentIcon;
    case AIActionKind.Process:
      return aiProcessIcon;
    case AIActionKind.Assistant:
      return chatBotIcon;
    default:
      return undefined;
  }
};

/** The groups with a header, in the order the menu shows them; Other follows them under a divider. */
const aiActionMenuSections: AIActionGroup[] = [AIActionGroup.Inline, AIActionGroup.Assistant, AIActionGroup.Background];

/** Whether a binding waits for the panel: it runs through the panel, and the panel is running something. */
const waitsForPanel = (binding: AIActionBinding, busy: boolean): boolean =>
  busy && binding.template.kind !== AIActionKind.Inline;

/** Whether a binding cannot run now: disabled by its scene, or waiting for a busy panel. */
export const isAIActionUnavailable = (binding: AIActionBinding, busy: boolean): boolean =>
  binding.disabled === true || waitsForPanel(binding, busy);

/**
 * The action the button names and runs: a background run that is going (the button then reads Stop), else the first
 * action of the first group with a header, else the first. Not the first that can run: the name would change under the
 * user's hand every time an action is briefly disabled (while it runs, while the field is empty). Undefined without
 * bindings.
 */
export const getPrimaryAIActionBinding = (bindings: AIActionBinding[]): AIActionBinding | undefined => {
  const running = bindings.find((binding) => binding.group === AIActionGroup.Background && binding.running === true);
  if (running !== undefined) return running;

  for (const group of aiActionMenuSections) {
    const first = bindings.find((binding) => binding.group === group);
    if (first !== undefined) return first;
  }

  return bindings[0];
};

/** The tooltip of an action: why it waits when the panel is busy, else its own title. */
export const getAIActionTitle = (binding: AIActionBinding, t: TFunction<string[]>, busy: boolean): string | undefined =>
  waitsForPanel(binding, busy) ? t('copilot:Actions.Busy') : binding.title;

/**
 * The grouped menu of several bindings: a header per group in render order, and what is left around a run
 * (the Other group) at the bottom under a divider. The button's menu and a command bar's overflow use it.
 * `unavailableTitle` greys every action with that tooltip (AI is off or not in the plan).
 */
export const getAIActionMenuItems = (
  bindings: AIActionBinding[],
  t: TFunction<string[]>,
  invoke: (binding: AIActionBinding) => void,
  // the panel of this tab is running something (isCopilotBusy): every item that runs through the panel waits
  busy: boolean = false,
  unavailableTitle?: string,
): IContextualMenuItem[] => {
  const toMenuItem = (binding: AIActionBinding): IContextualMenuItem => ({
    key: binding.id,
    text: getAIActionLabel(binding, t),
    title: unavailableTitle ?? getAIActionTitle(binding, t, busy),
    iconProps: getAIActionIcon(binding),
    disabled: unavailableTitle !== undefined || isAIActionUnavailable(binding, busy),
    onClick: () => invoke(binding),
  });
  const getHeader = (group: AIActionGroup): string => {
    switch (group) {
      case AIActionGroup.Inline:
        return t('copilot:AIButton.Sections.Inline');
      case AIActionGroup.Assistant:
        return t('copilot:AIButton.Sections.Assistant');
      default:
        return t('copilot:AIButton.Sections.Background');
    }
  };
  const menuItems: IContextualMenuItem[] = [];

  for (const group of aiActionMenuSections) {
    const sectionBindings = bindings.filter((binding) => binding.group === group);
    if (sectionBindings.length === 0) continue;

    menuItems.push({
      key: `header-${group}`,
      text: getHeader(group),
      itemType: IPContextualMenuItemType.Header,
    });
    menuItems.push(...sectionBindings.map(toMenuItem));
  }

  const otherBindings = bindings.filter((binding) => binding.group === AIActionGroup.Other);
  if (otherBindings.length > 0) {
    if (menuItems.length > 0) menuItems.push({ key: 'otherDivider', itemType: IPContextualMenuItemType.Divider });
    menuItems.push(...otherBindings.map(toMenuItem));
  }

  return menuItems;
};

/** The settings page that holds the AI switch, and the subscription page. */
const aiSettingsRoute: string = '/admin/settings/automation';
const subscriptionRoute: string = '/admin/subscription';

/** Switches AI on for the organization, as the panel's own switch does; the app re-renders through the bridge. */
const enableAI = async (appContext: IAppContext, t: TFunction<string[]>) => {
  try {
    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    const setting = await apiSetSetting(AIEnabled, true, accessToken);
    if (!setting) return;

    appContext.globalDataCache.settings.set(AIEnabled, true);
    appContext.copilotBridge.notifyStateChanged();
    appContext.showNotification(t('copilot:AIButton.Off.Enabled'));
  } catch (err) {
    appContext.setError(err);
  }
};

/** Copies a link to a page for someone who can act on it: the AI switch, or the subscription. */
const copyLinkForAdmin = async (appContext: IAppContext, t: TFunction<string[]>, route: string) => {
  try {
    await navigator.clipboard.writeText(`${window.location.origin}${route}`);
    appContext.showNotification(t('copilot:AIButton.Off.Copied'));
  } catch {
    appContext.showNotification(t('copilot:AIButton.Off.CopyFailed'), true);
  }
};

/**
 * The menu while AI cannot run: what is going on and the one thing to do about it at the top, the actions of this page
 * under it, greyed, so the user sees what AI would do here.
 * - AI off, a manager: switch it on here (the same switch as Settings > Automation; the actions work right after).
 * - AI off, anyone else: copy a link to that switch for a manager.
 * - No AI in the plan: the subscription page for whoever may change it, else a link to it for them.
 */
export const getAIUnavailableMenuItems = (
  access: 'off' | 'plan',
  bindings: AIActionBinding[],
  t: TFunction<string[]>,
  appContext: IAppContext,
  navigate: (route: string) => void,
): IContextualMenuItem[] => {
  const isPlan: boolean = access === 'plan';
  const canAct: boolean = isPlan
    ? hasUserFeature(appContext, FeatureTypes.AdminSubscription)
    : hasUserFeatureGenericManager(appContext);
  const route: string = isPlan ? subscriptionRoute : aiSettingsRoute;

  let action: IContextualMenuItem;
  if (!canAct) {
    action = {
      key: 'ai-copy-link',
      text: t('copilot:AIButton.Off.CopyLink'),
      title: isPlan ? t('copilot:AIButton.NoPlan.AskInfo') : t('copilot:AIButton.Off.AskInfo'),
      iconProps: { iconName: 'Link' },
      onClick: () => void copyLinkForAdmin(appContext, t, route),
    };
  } else if (isPlan) {
    action = {
      key: 'ai-subscription',
      text: t('copilot:AIButton.NoPlan.View'),
      title: t('copilot:AIButton.Info'),
      iconProps: { iconName: 'ShoppingCart' },
      onClick: () => navigate(route),
    };
  } else {
    action = {
      key: 'ai-enable',
      text: t('copilot:AIButton.Off.Enable'),
      title: t('copilot:AIButton.Info'),
      iconProps: { iconName: 'Settings' },
      onClick: () => void enableAI(appContext, t),
    };
  }

  return [
    {
      key: 'ai-unavailable-header',
      text: isPlan ? t('copilot:AIButton.NoPlan.Title') : t('copilot:AIButton.Off.Title'),
      itemType: IPContextualMenuItemType.Header,
    },
    action,
    { key: 'ai-unavailable-divider', itemType: IPContextualMenuItemType.Divider },
    ...getAIActionMenuItems(
      bindings,
      t,
      () => {},
      false,
      isPlan ? t('copilot:AIButton.NoPlan.Reason') : t('copilot:AIButton.Off.Reason'),
    ),
  ];
};

export enum AIActionsButtonVariant {
  CommandBar,
  Default,
  // the AI badge alone, for a surface without room for a label
  Icon,
  // link-like, for a header above a field
  Action,
}

interface IAIActionsButtonProps extends IPButtonProps {
  variant: AIActionsButtonVariant;
  // Everything AI does on this surface. The caller decides the list; this component renders it.
  bindings: AIActionBinding[];
  // A text field: the field and the text it holds when the user clicks, sent along with a panel-run action.
  getActiveField?: () => CopilotActiveField | undefined;
}

/**
 * One AI entry point per surface, with the AI badge so it is recognisable everywhere. On a command bar, above a field
 * (`Action`) and as the badge alone (`Icon`) it reads "AI actions" and opens the menu, grouped by what each action
 * does for the user, whatever the number of actions. On a card (`Default`) the button names the primary action
 * (`getPrimaryAIActionBinding`) and runs it; with more actions it is a split button whose chevron opens all of them.
 * While AI cannot run (off, or not in the plan)
 * the button stays, opens the menu, and the menu says why and what to do about it (`getAIUnavailableMenuItems`).
 * Nothing to offer renders nothing.
 */
export const AIActionsButton = (props: IAIActionsButtonProps) => {
  const { variant, getActiveField, ...buttonProps } = props;
  const { t } = useTranslation(['copilot']);
  const appContext = useContext(AppContext);
  const history = useHistory();
  const bindings = getOfferedAIActionBindings(props.bindings, appContext);

  if (bindings.length === 0) return null;

  const getButton = () => {
    switch (variant) {
      case AIActionsButtonVariant.CommandBar:
        return IPCommandBarButton;
      case AIActionsButtonVariant.Icon:
        return IPIconButton;
      case AIActionsButtonVariant.Action:
        return IPActionButton;
      default:
        return IPDefaultButton;
    }
  };
  const Button = getButton();
  const access: AIAccess = getAIAccess(appContext);
  const isRunning = bindings.some((binding) => binding.group === AIActionGroup.Background && binding.running === true);
  const busy: boolean = isCopilotBusy(appContext);
  const primary = getPrimaryAIActionBinding(bindings) as AIActionBinding;
  const invoke = (binding: AIActionBinding) => invokeAIAction(binding, appContext, getActiveField);
  const menuItems: IContextualMenuItem[] =
    access === 'off' || access === 'plan'
      ? getAIUnavailableMenuItems(access, bindings, t, appContext, (route) => history.push(route))
      : getAIActionMenuItems(bindings, t, invoke, busy);

  // The badge alone has no room for a name, and a field header keeps the short one: both open the menu.
  if (variant === AIActionsButtonVariant.Icon) {
    return (
      <Button {...buttonProps} title={t('copilot:AIButton.Label')} iconProps={copilotIconProps} menuProps={{ items: menuItems }} />
    );
  }

  // A command bar reads "AI actions" whatever the count (Chris, 2026-10-24, restored 2026-11-02)
  const namesPrimary: boolean = variant === AIActionsButtonVariant.Default;
  const label: string = namesPrimary ? getAIActionLabel(primary, t) : t('copilot:AIButton.Label');

  // The label carries the run indicator: a spinner in the icon slot would replace the AI badge, which
  // is what makes this button recognizable across surfaces.
  const renderText = () => (
    <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        {/* Inherits the button's colour, so a disabled primary reads grey like the bar's other disabled commands. */}
        <IPText styles={{ root: { color: 'inherit' } }}>{label}</IPText>
      </IPStack.Item>
      {isRunning && (
        <IPStack.Item>
          <IPSpinner size={IPSpinnerSize.xSmall} />
        </IPStack.Item>
      )}
    </IPStack>
  );

  // Runs the primary: available, and on a surface that names it. Otherwise the button opens the menu.
  if (access !== 'available' || !namesPrimary) {
    return (
      <Button
        {...buttonProps}
        text={label}
        title={t('copilot:AIButton.Label')}
        onRenderText={renderText}
        iconProps={copilotIconProps}
        menuProps={{ items: menuItems }}
      />
    );
  }

  const hasMenu: boolean = bindings.length > 1;
  const unavailable: boolean = isAIActionUnavailable(primary, busy);
  // The badge is an image, which the disabled colour does not reach: it fades with a primary that cannot run.
  const iconProps: IIconProps = unavailable
    ? { ...copilotIconProps, styles: { root: { opacity: 0.4 } } }
    : copilotIconProps;

  return (
    <Button
      {...buttonProps}
      text={label}
      title={getAIActionTitle(primary, t, busy) ?? label}
      onRenderText={renderText}
      iconProps={iconProps}
      onClick={() => invoke(primary)}
      split={hasMenu}
      splitButtonAriaLabel={t('copilot:AIButton.Label')}
      primaryDisabled={hasMenu && unavailable}
      disabled={!hasMenu && unavailable}
      menuProps={hasMenu ? { items: menuItems } : undefined}
    />
  );
};
